import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine } from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import api from "@/lib/api";
import { useServerTime } from "@/hooks/useServerTime";
import { fmtNum, STATUS_HEX } from "@/lib/format";

const inr = (n) => (n == null ? "—" : `₹${fmtNum(Math.round(n))}`);
const short = (v) => (v >= 10000000 ? `${(v / 10000000).toFixed(1)}Cr` : v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v);
const statusOf = (pct) => (pct == null ? "pending" : pct >= 100 ? "green" : pct >= 80 ? "amber" : "red");

const TT = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 text-xs shadow-xl">
      <div className="font-semibold mb-1">{label} · {d.date}</div>
      <div className="font-mono space-y-0.5 text-slate-700">
        <div>Earned {inr(d.earned)}</div>
        <div>Daily target {inr(d.daily_target)}{d.pct != null ? ` · ${d.pct}%` : ""}</div>
        {d.actual != null && <div>Output {fmtNum(d.actual)} / {fmtNum(d.target)}</div>}
      </div>
    </div>
  );
};

export function MvaTrend({ plantId, reportingDate }) {
  const [sec, setSec] = useState("ALL");
  const serverTime = useServerTime();
  const q = useQuery({
    queryKey: ["mva-trend", plantId, reportingDate],
    queryFn: async () => (await api.get("/mva-trend", { params: { end_date: reportingDate, days: 7, plant_id: plantId || undefined } })).data,
  });
  const t = q.data;
  const rows = (t?.series || []).map((s) => {
    if (sec === "ALL") return { ...s, actual: null };
    const b = s.by_section[sec] || {};
    return { date: s.date, label: s.label, earned: b.earned || 0, daily_target: b.daily_target, pct: b.pct, actual: b.actual, target: b.target };
  });
  const avg = rows.length ? rows.reduce((a, r) => a + r.earned, 0) / Math.max(rows.filter((r) => r.earned > 0).length, 1) : 0;
  const complete = rows.filter((r) => r.date !== serverTime?.today);
  const last = complete[complete.length - 1], prev = complete[complete.length - 2];
  const momentum = last && prev && prev.earned ? ((last.earned - prev.earned) * 100) / prev.earned : null;
  const onTarget = rows.filter((r) => r.pct != null && r.pct >= 100).length, withTarget = rows.filter((r) => r.daily_target).length;

  return (
    <div className="pp-card p-5 rise rise-3" data-testid="mva-trend-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <div className="pp-label">7-day MVA trend</div>
          <div className="text-sm text-slate-500">Earned per day (bars) vs daily target (purple) · dashed = 7-day average</div>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="mva-trend-tabs">
          {["ALL", ...(t?.sections || [])].map((s) => (
            <button key={s} onClick={() => setSec(s)} data-testid={`mva-trend-tab-${s}`} className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${sec === s ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-300 text-slate-500 hover:bg-slate-100"}`}>{s === "ALL" ? "All sections" : s}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="mva-trend-momentum">
          <div className="pp-label">Momentum (last full day vs previous)</div>
          <div className={`font-mono text-xl font-bold flex items-center gap-1.5 ${momentum == null ? "text-slate-400" : momentum >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
            {momentum == null ? <Minus size={18} /> : momentum >= 0 ? <TrendingUp size={18} /> : <TrendingDown size={18} />}{momentum == null ? "—" : `${momentum >= 0 ? "+" : ""}${momentum.toFixed(1)}%`}
          </div>
        </div>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="mva-trend-avg">
          <div className="pp-label">7-day avg earned</div>
          <div className="font-mono text-xl font-bold">{inr(avg)}</div>
        </div>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="mva-trend-days">
          <div className="pp-label">Days on target</div>
          <div className="font-mono text-xl font-bold">{withTarget ? `${onTarget}/${withTarget}` : "—"}<span className="text-xs text-slate-400 font-normal ml-1">{withTarget ? "" : "no targets set"}</span></div>
        </div>
      </div>
      <div className="h-64 w-full min-h-[256px]" data-testid="mva-trend-chart">
        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 600, height: 240 }} minHeight={200}>
          <ComposedChart data={rows} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
            <CartesianGrid stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 12, fontFamily: "IBM Plex Mono" }} />
            <YAxis tick={{ fill: "#64748B", fontSize: 11, fontFamily: "IBM Plex Mono" }} tickFormatter={short} />
            <Tooltip content={<TT />} cursor={{ fill: "rgba(100,116,139,0.08)" }} />
            <Legend wrapperStyle={{ fontSize: 12, color: "#64748B" }} />
            <Bar dataKey="earned" name="MVA earned (₹)" radius={[4, 4, 0, 0]} maxBarSize={56}>
              {rows.map((r, i) => <Cell key={i} fill={r.daily_target ? STATUS_HEX[statusOf(r.pct)] : "#3B82F6"} />)}
            </Bar>
            <Line type="monotone" dataKey="daily_target" name="Daily target (₹)" stroke="#7C3AED" strokeWidth={2} dot={{ r: 4, fill: "#7C3AED" }} connectNulls />
            {avg > 0 && <ReferenceLine y={avg} stroke="#0F172A" strokeDasharray="6 3" label={{ value: `avg ${short(Math.round(avg))}`, position: "insideTopRight", fill: "#0F172A", fontSize: 11 }} />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5" data-testid="mva-trend-strip">
        {rows.map((r) => (
          <span key={r.date} data-testid={`mva-trend-day-${r.date}`} className={`rounded border px-2 py-0.5 font-mono text-[11px] status-${r.daily_target ? statusOf(r.pct) : r.earned > 0 ? "pending" : "missing"}`} title={`${r.date}: ${inr(r.earned)}${r.pct != null ? ` (${r.pct}%)` : ""}`}>{r.label}{r.pct != null ? ` ${r.pct}%` : ""}{r.date === serverTime?.today ? " · so far" : ""}</span>
        ))}
      </div>
    </div>
  );
}
